import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Progress, Spin, notification, Modal } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { nanoid } from 'nanoid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { t } from '../../i18n/i18n';
import {
  getPsychotechnicByScreeningInstanceId,
  getScreeningByInstanceId,
  getSubmissionAnswers,
  getTestGroups,
  getTestGroupsDetailsBatch,
} from '../../API/services/screeningService';
import { setInvitedProfileTestGroups } from '../../redux/actions/profilesActions';
import { getIntegerPart, oneDecimal } from '../../utils/numbersUtils';
import { setSubmissionSelected } from '../../redux/actions/screeningActions';
import InvitedProfileComments from '../InvitedProfileComments/InvitedProfileComments';
import Collapsed from '../Collapsed/Collapsed';
import './InvitedProfileStages.scss';
import EmptyComponent from '../EmptyComponent/EmptyComponent';
import {
  getCandidatePdfExport,
  getCandidateQualitativePdfExport,
  getProfileSkills,
  getPsychotechnicExportReport,
  getPsychotechnicExportReportAdditional,
} from '../../API/services/alkymersService';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import InvitedProfileCommunications from '../InvitedProfileCommunications/InvitedProfileCommunications';
import CloudDownload from '../../assets/dashboard/CloudDownload';
import closeIcon from '../../assets/createSearch/close-icon.svg';
import { isNullOrUndefined } from '../../utils/typesUtils';
import {
  DashboardAccessLevel,
  NPSProcessTypes,
  ProcessStatusStage,
  ProfileDataTypes,
  PromptSubTypes,
  ReviewTypeEnum,
  TestGroupMetricsRol,
  TestGroupTypes,
} from '../../constants/enums';
import QualitativeCollapsed from './QualitativeCollapsed/QualitativeCollapsed';
import Spinner from '../spinner/Spinner';
import ApplicationMethod from './ApplicationMethod/ApplicationMethod';
import ProfileInvitationsAndChallenges from './ProfileInvitationsAndChallenges/ProfileInvitationsAndChallenges';
import DropdownSelectDot from '../DropdownSelectDot/DropdownSelectDot';
import {
  INVITED_PROFILES_PROCTORING_DETAIL,
  PUBLIC_INVITED_PROFILES_PROCTORING_DETAIL,
  SCREENING_SEARCHES,
} from '../../constants/routes';
import DropdownSelect from '../DropdownSelect/DropdownSelect';
import useTestGroupMetrics from '../../hooks/useTestGroupMetrics';
import { getPercentile } from '../../utils/stats/statistics';
import { downloadFile, downloadBase64Pdf } from '../../utils/downloadFile';
import { isSFIATestGroup, someSFIATestGroup, allSFIATestGroups } from '../../utils/testgroups';
import { setNPSProcessType } from '../../redux/actions/npsActions';
import useTranslations from '../../hooks/useTranslations';

const InvitedProfileStages = ({
  setShow,
  profile,
  isInvitedProfile,
  score,
  setGroupAcceleration,
  updateStageSingleCandidate,
  uniqueProfileData,
}) => {
  const { key: langKey } = useTranslations();
  const [selectedStage] = useState(0);
  const [collapsedSelected, setCollapsedSelected] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(undefined);
  const [loadingTestGroups, setLoadingTestGroups] = useState(isInvitedProfile);
  const { currentWorkspace, partner, isPublicDashboard, publicDashboardGuest } = useSelector(
    (state) => state.auth
  );
  const [screeningInstanceId, setScreeningInstanceId] = useState('');
  const [prefilterInstanceId, setPrefilterInstanceId] = useState('');
  const [screeningInstance, setScreeningInstance] = useState(null);
  const [psychotechnic, setPsychotechnic] = useState(null);
  const [groupsProportion, setGroupsProportion] = useState({});
  const { invitedProfileTestGroups: testGroups } = useSelector((state) => state.profiles);
  const [resultsToShow, setResultsToShow] = useState(1);
  const [percentValue, setPercentValue] = useState(0);
  const {
    roleMetricSelected,
    companyName,
    companyId,
    companyMetricSelected,
    setCompanyMetricSelected,
  } = useTestGroupMetrics({
    testGroup: {},
  });
  const dispatch = useDispatch();
  const navigate = useHistory();
  const [sfiaAverage, setSfiaAverage] = useState({ value: 0, percent: 0 });

  const isEvaluation = uniqueProfileData?.type === ProfileDataTypes.EVALUATION;
  const isChallenge = uniqueProfileData?.type === ProfileDataTypes.CHALLENGE;
  const screeningName = isEvaluation
    ? uniqueProfileData?.data?.[0]?.invitation?.screeningName
    : undefined;
  const invitationData = uniqueProfileData?.data?.[selectedStage]?.invitation ?? null;
  const visibleTechnologies = profile?.technologies?.filter((tech) => tech?.technology?.visible);
  const [qualitativeResultType, setQualitativeResultType] = useState('');
  const invitedDate =
    invitationData && uniqueProfileData?.data?.[selectedStage]?.invitation?.createdAt
      ? format(
        new Date(uniqueProfileData?.data?.[selectedStage]?.invitation?.createdAt),
        'dd/MM/yyyy',
        { locale: es || undefined }
      )
      : '-';
  const submittedDate =
    invitationData && uniqueProfileData?.data?.[selectedStage]?.invitation?.endDate
      ? format(
        new Date(uniqueProfileData?.data?.[selectedStage]?.invitation?.endDate),
        'dd/MM/yyyy',
        { locale: es || undefined }
      )
      : '-';

  const getPonderatedPercentil = () => {
    const testgroupOfInstance = testGroups[screeningInstanceId];
    const percentajeWithMetrics = testgroupOfInstance
      ?.filter(
        ({ type, metrics }) =>
          (type === TestGroupTypes.QUANTITATIVE ||
            type === TestGroupTypes.CUSTOM ||
            type === TestGroupTypes.CHALLENGE_ASYNC_AI) &&
          metrics?.dataGrouped?.length
      )
      ?.reduce((sum, t) => sum + t.weight, 0);
    const totalWeightedSum = testgroupOfInstance
      ?.filter(
        ({ type, metrics }) =>
          (type === TestGroupTypes.QUANTITATIVE ||
            type === TestGroupTypes.CUSTOM ||
            type === TestGroupTypes.CHALLENGE_ASYNC_AI) &&
          metrics?.dataGrouped?.length
      )
      ?.reduce((sum, t) => {
        const metricDataSelected = t?.metrics?.dataGrouped?.find(
          (d) => d.companyId === companyMetricSelected
        )?.roleMetrics[roleMetricSelected];
        if (metricDataSelected) {
          const percentile = getPercentile(t?.score, metricDataSelected?.percentiles);
          return sum + (percentile * t.weight) / percentajeWithMetrics;
        }
        return sum;
      }, 0);
    return totalWeightedSum;
  };

  const getScreeningInstance = async (screeningInstanceIdCopy) => {
    try {
      const screeningInstanceCopy = await getScreeningByInstanceId(screeningInstanceIdCopy);
      setScreeningInstance(screeningInstanceCopy);
      setCandidateRole(screeningInstanceCopy?.candidateRole ?? '');
      const psychotechnic = await getPsychotechnicByScreeningInstanceId(screeningInstanceIdCopy);
      setPsychotechnic(psychotechnic);
    } catch (e) {
      console.log(e);
      navigate.push(SCREENING_SEARCHES);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isMounted) return;

      setLoadingTestGroups(true);
      const testgroupsIds = uniqueProfileData?.data
        ?.map((stage) => stage?.invitation?.screeningInstanceId)
        .filter((id) => Boolean(id));
      let testGroups = [];

      try {
        if (testgroupsIds.length) {
          testGroups = await getTestGroups(testgroupsIds);
        }

        if (!isMounted) return;

        if (uniqueProfileData?.data?.[0]?.invitation?.prefilterInstanceId) {
          const prefilterTest = await getTestGroups(
            uniqueProfileData?.data?.[0]?.invitation?.prefilterInstanceId
          );
          if (!isMounted) return;

          testGroups?.unshift(prefilterTest[0]);
          setPrefilterInstanceId(uniqueProfileData?.data?.[0]?.invitation?.prefilterInstanceId);
        }

        if (testGroups.length && isMounted) {
          const setAveragePercent = (testgroups) => {
            if (!testgroups.length) return { value: 0, percent: 0 };
            const validScores = testgroups
              .map((tg) => Number(tg?.results?.score))
              .filter((score) => !isNaN(score) && score >= 1 && score <= 7);

            if (!validScores.length) return { value: 0, percent: 0 };

            const avg = validScores.reduce((sum, score) => sum + score, 0) / validScores.length;
            const value = Math.round(Math.max(1, Math.min(7, avg)));
            const percent = ((value - 1) / 6) * 100;
            return { value, percent };
          };

          if (testGroups?.some((x) => x?.testGroups?.some((tg) => isSFIATestGroup(tg)))) {
            setCollapsedSelected([0]);
          }

          if (
            testGroups?.some((x) =>
              x?.testGroups?.some((tg) => tg.type === TestGroupTypes.QUALITATIVE)
            )
          ) {
            setCollapsedSelected([0]);
            const qualitativeTestGroupIds = testGroups
              ?.flatMap((x) => x.testGroups)
              .filter((tg) => tg.type === TestGroupTypes.QUALITATIVE)
              .map((tg) => tg.id);
            const qualitativeTestGroupsData = await getTestGroupsDetailsBatch(
              qualitativeTestGroupIds
            );

            if (!isMounted) return;

            qualitativeTestGroupsData.forEach((test) => {
              const instanceTestGroups = testGroups.find(
                (tg) =>
                  tg.id ===
                  uniqueProfileData?.data?.[selectedStage]?.invitation?.screeningInstanceId
              ).testGroups;
              const testGroup = instanceTestGroups.find((tg) => tg.id === test.id);
              testGroup.completeData = test;
            });
          }

          const formated = testGroups?.reduce(
            (object, instance) => ({ ...object, [instance.id]: instance.testGroups }),
            {}
          );
          const proportions = {};
          Object.entries(formated).forEach(([id, testGroup]) => {
            const totalWeight = testGroup.reduce((sum, test) => sum + test.weight, 0);
            proportions[id] = 100 / totalWeight;
          });

          if (isMounted) {
            setLoadingTestGroups(false);
            setGroupsProportion(proportions);
            dispatch(setInvitedProfileTestGroups(formated));
            const screeningInstanceIdCopy =
              uniqueProfileData?.data?.[selectedStage]?.invitation?.screeningInstanceId;
            if (screeningInstanceIdCopy) {
              const currentTestGroups = formated[screeningInstanceIdCopy] || [];
              if (allSFIATestGroups(currentTestGroups)) {
                const avgObj = setAveragePercent(currentTestGroups);
                setPercentValue(avgObj.percent);
                setSfiaAverage(avgObj);
              } else {
                const percentValue =
                  uniqueProfileData?.recruitSession?.reviewType === ReviewTypeEnum.PSYCHOTECHNICS
                    ? 100
                    : resultsToShow === 1
                      ? isEvaluation
                        ? uniqueProfileData?.data?.[selectedStage]?.invitation?.score ?? 0
                        : Math.round(
                          uniqueProfileData?.data?.find((technology) => technology?.isPrimary)
                            ?.bestScore
                        )
                      : oneDecimal(getPonderatedPercentil());
                setPercentValue(percentValue);
                setSfiaAverage({ value: 0, percent: 0 });
              }
              setScreeningInstanceId(screeningInstanceIdCopy);
              getScreeningInstance(screeningInstanceIdCopy);
            }
          }
        } else if (isMounted) {
          setLoadingTestGroups(false);
        }
      } catch (error) {
        if (isMounted) {
          setLoadingTestGroups(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
      dispatch(setInvitedProfileTestGroups({}));
    };
  }, [uniqueProfileData.data]);

  useEffect(() => {
    let isMounted = true;

    const screeningInstanceIdCopy =
      uniqueProfileData?.data?.[selectedStage]?.invitation?.screeningInstanceId;
    setScreeningInstanceId(screeningInstanceIdCopy);
    if (screeningInstanceIdCopy) {
      getScreeningInstance(screeningInstanceIdCopy, () => isMounted);
    }

    return () => {
      isMounted = false;
    };
  }, [selectedStage]);

  const getDataSubmission = async (screeningInstanceId, testGroup, index) => {
    setLoadingDetail(index);
    await getSubmissionAnswers(screeningInstanceId, testGroup?.id).then((res) => {
      dispatch(setSubmissionSelected({ testGroup, submissions: res }));
      setLoadingDetail(undefined);
      setShow(true);
    });
  };

  const screeningInstanceWithSfiaTestgroup = testGroups[screeningInstanceId]?.some((testgroup) =>
    testgroup.tests.every(
      (test) =>
        (test?.promptSubType ?? test?.test?.promptSubType) === PromptSubTypes.SFIA_CORRECTION
    )
  );

  const optionsCollapsed = [
    {
      title: t('PROFILE_QUALITITAVE_RESULTS'),
      content: (
        <>
          {testGroups[screeningInstanceId]?.some(
            (testgroup) => testgroup.type === TestGroupTypes.QUALITATIVE
          ) || screeningInstanceWithSfiaTestgroup ? (
            <QualitativeCollapsed
              testgroups={testGroups[screeningInstanceId]?.filter(
                (testgroup) =>
                  testgroup.type === TestGroupTypes.QUALITATIVE ||
                  testgroup.tests.every(
                    (test) =>
                      (test?.promptSubType ?? test?.test?.promptSubType) ===
                      PromptSubTypes.SFIA_CORRECTION
                  )
              )}
              recruitSessionId={uniqueProfileData?.data?.[0]?.recruitSessionStage?.recruitSessionId}
              profile={profile}
              qualitativeResultType={qualitativeResultType}
              setQualitativeResultType={setQualitativeResultType}
              screeningInstance={screeningInstance}
              setScreeningInstance={setScreeningInstance}
            />
          ) : (
            <EmptyComponent
              title={t('INVITED_PROFILE_EMPTY_TITLE_SECTION')}
              className="my-3 align-items-center py-3"
            />
          )}
        </>
      ),
      isVisible: testGroups[screeningInstanceId]?.some(
        (testgroup) =>
          testgroup.type === TestGroupTypes.QUALITATIVE ||
          testgroup.tests.every(
            (test) =>
              (test?.promptSubType ?? test?.test?.promptSubType) === PromptSubTypes.SFIA_CORRECTION
          )
      ),
      collapsedEnabled: true,
    },
    {
      title: t('PROFILE_COMMENTS'),
      content: <InvitedProfileComments isInvitedProfile={isInvitedProfile} />,
      isVisible: true,
      collapsedEnabled: false,
      containerClassname: 'py-0 mt-3',
    },
    {
      title: t('SEARCH_COMMUNICATION_HISTORY_TITLE'),
      content: <InvitedProfileCommunications isInvitedProfile={isInvitedProfile} />,
      isVisible: isInvitedProfile && !isPublicDashboard,
      collapsedEnabled: false,
      containerClassname: 'py-0 mt-3  mx-auto pt-3 b-radius-8',
      titleClassnames: 'px-4 justify-content-between  font-weight-600 text-base text-black pb-2',
    },
    {
      title: t('PROFILE_METHOD_APPLIED'),
      content: (
        <>
          <ApplicationMethod recruitSessionStage={invitationData} />
        </>
      ),
      isVisible: true,
      collapsedEnabled: true,
      containerClassname: 'py-0 mt-3  mx-auto pt-3 b-radius-8',
      titleClassnames: 'px-4 justify-content-between  font-weight-600 text-base text-black pb-2',
    },
  ];

  const setProfileNotInvited = async (progressTech, index) => {
    setGroupAcceleration({});
    dispatch(setSubmissionSelected({}));
    setLoadingDetail(index);
    try {
      if (!progressTech?.screeningInstanceId) {
        await getProfileSkills(progressTech?.id).then((res) => {
          setGroupAcceleration({ ...progressTech, skills: res.skills });
          setLoadingDetail(undefined);
          setShow(true);
        });
      } else {
        const screeningInstanceCopy = await getScreeningByInstanceId(
          progressTech?.screeningInstanceId
        );
        if (screeningInstanceCopy) {
          await getDataSubmission(
            progressTech?.screeningInstanceId,
            screeningInstanceCopy.testGroups[0],
            index
          );
        }
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingDetail(undefined);
    }
  };

  const [reportLoading, setReportLoading] = useState(false);
  const [candidateRole, setCandidateRole] = useState(screeningInstance?.candidateRole ?? '');
  const [modalCandidateRole, setModalCandidateRole] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [pendingReportType, setPendingReportType] = useState(null);

  const getPsychotechnicReportAdditional = async (screeningInstanceId) => {
    try {
      return await getPsychotechnicExportReportAdditional(screeningInstanceId);
    } catch (error) {
      console.error('Error al obtener el reporte adicional:', error);
      return null;
    }
  };

  const generatePsychotechnicReport = async () => {
    setReportLoading(true);
    try {
      const [mainPdf, additionalPdf] = await Promise.all([
        getPsychotechnicExportReport(screeningInstanceId),
        getPsychotechnicReportAdditional(screeningInstanceId),
      ]);

      if (!mainPdf) return;

      const mainBlob = window.URL.createObjectURL(mainPdf);
      const mainLink = document.createElement('a');
      mainLink.href = mainBlob;
      mainLink.setAttribute('download', 'psychotechnic.pdf');
      mainLink.setAttribute('target', '_blank');
      document.body.appendChild(mainLink);
      mainLink.click();
      document.body.removeChild(mainLink);

      if (additionalPdf && additionalPdf.size > 0) {
        const additionalBlob = window.URL.createObjectURL(additionalPdf);
        const additionalLink = document.createElement('a');
        additionalLink.href = additionalBlob;
        additionalLink.setAttribute('download', 'psychotechnic-additional.pdf');
        additionalLink.setAttribute('target', '_blank');
        document.body.appendChild(additionalLink);
        additionalLink.click();
        document.body.removeChild(additionalLink);
      }
    } catch (error) {
      console.error('Error descargando los reportes:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const showDownloadButton = () => {
    if (screeningInstance?.screening?.reviewType === ReviewTypeEnum.PSYCHOTECHNICS) {
      return psychotechnic?.status === 'FINISHED';
    }
    return true;
  };

  const canDownloadMainReport =
    !loadingTestGroups &&
    uniqueProfileData?.data?.[0]?.recruitSessionStage?.recruitSessionId &&
    profile?.id &&
    showDownloadButton();

  const downloadOptions = [
    {
      id: false,
      name: t('DOWNLOAD_RESUMED_REPORT'),
    },
  ];
  if (publicDashboardGuest?.accessType !== DashboardAccessLevel.LIMITED_ACCESS) {
    downloadOptions.push({
      id: true,
      name: t('DOWNLOAD_FULL_REPORT'),
    });
  }

  const generateUserReport = async (completeReport = true, providedCandidateRole = null) => {
    setReportLoading(true);
    if (screeningInstance?.screening?.reviewType === ReviewTypeEnum.PSYCHOTECHNICS) {
      if (psychotechnic?.status === 'FINISHED') {
        generatePsychotechnicReport();
      } else {
        notification.open({
          message: t('PSYCHOTECHNIC_WAITING'),
          type: 'info',
        });
        setReportLoading(false);
      }
      return;
    }

    const newVersionOfQualitative =
      screeningInstanceId &&
      testGroups[screeningInstanceId][0]?.completeData?.qualitativeConfigResults &&
      !testGroups[screeningInstanceId][0]?.completeData?.qualitativeConfigResults?.type;

    try {
      const recruitSessionId = uniqueProfileData?.data?.[0]?.recruitSessionStage?.recruitSessionId;
      const profileId = encodeURI(profile?.id);
      const res = await getCandidatePdfExport(recruitSessionId, profileId, completeReport, providedCandidateRole);
      const blob = window.URL.createObjectURL(res);
      const fileName = `${profile?.firstName?.toLowerCase()}-${profile?.lastName?.toLowerCase()}.pdf`;
      downloadFile(blob, fileName);
    } catch (error) {
      console.error('Error generating candidate report:', error);
    }
    try {
      if (newVersionOfQualitative) {
        const recruitSessionId =
          uniqueProfileData?.data?.[0]?.recruitSessionStage?.recruitSessionId;
        const profileId = encodeURI(profile?.id);

        const res = await getCandidateQualitativePdfExport({
          recruitSessionId,
          profileId,
          demo: false,
        });
        res?.forEach((base64String, index) => {
          const filename = `${profile.firstName.toLowerCase()}-${profile.lastName.toLowerCase()}-qualitative-${index + 1
            }.pdf`;
          downloadBase64Pdf(base64String, filename);
        });
      }
    } catch (error) {
      console.error('Error generating qualitative report:', error);
    } finally {
      setReportLoading(false);
      dispatch(setNPSProcessType(NPSProcessTypes.REPORT_DOWNLOADED));
    }
  };

  const addCandidateRole = async () => {
    if (!screeningInstance?.candidateRole) {
      setScreeningInstance({ ...screeningInstance, candidateRole });
    }
    setModalCandidateRole(false);
    await generateUserReport(pendingReportType, screeningInstance?.candidateRole ?? candidateRole);
  };

  const selectDownloadReportOption = async (completeReport) => {
    if (screeningInstance?.candidateRole || candidateRole) {
      await generateUserReport(completeReport, screeningInstance?.candidateRole ?? candidateRole);
    } else {
      document.activeElement?.blur();
      setCandidateRole('');
      setPendingReportType(completeReport);
      setModalCandidateRole(true);
    }
  };

  const prefilterTestGroupPreview = (titleLabel) => (
    <>
      <div data-testid="prefilter-tests-container">
        {testGroups[prefilterInstanceId]?.map((testGroup, index) => (
          <>
            <div className="d-flex flex-column mt-3">
              <div className="d-flex flex-row align-items-center bg-white">
                <span className=" text-xs font-weight-bold text-capitalize container-name-mw">
                  {t(titleLabel)}
                </span>
                <button
                  className="border-0 bg-transparent cursor-pointer text-decoration-none d-flex button-detail-mw"
                  onClick={() => getDataSubmission(prefilterInstanceId, testGroup, index - 1)}
                  type="button"
                >
                  {!(loadingDetail === index - 1) ? (
                    <span className=" font-weight-600 span-see-detail-modal text-xs ml-2">
                      {t('DEV_PROFILE_TESTGROUP_VIEW_DETAIL')}
                    </span>
                  ) : (
                    <Spin
                      indicator={
                        <LoadingOutlined
                          style={{
                            fontSize: 18,
                          }}
                          spin
                        />
                      }
                      className="ml-2"
                    />
                  )}
                </button>
              </div>
              <Progress
                strokeWidth={8}
                className="invited-profile-stages-progress"
                strokeColor={{
                  '30%': '#4987BE',
                  '100%': '#9BF558',
                }}
                percent={testGroup.score * 100 ?? 0}
                format={() => (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {isEvaluation ? `pts ${Math.round(testGroup.score * 100)}/100` : ''}
                      </Tooltip>
                    }
                  >
                    <span className=" font-weight-bold invited-profile-skill-title pl-3 text-xs points-text">
                      {`${invitationData.prefilterScore === 1
                          ? t('PREFILTER_SCREENING_APPROVED')
                          : invitationData.prefilterScore === 0.5
                            ? t('PREFILTER_SCREENING_PENDING_REVIEW')
                            : t('SCREENINGS_INVITATIONS_PREFILTERED')
                        } `}
                    </span>
                  </OverlayTrigger>
                )}
              />
            </div>
          </>
        ))}
      </div>
    </>
  );
  const processStatusOptions = Object.values(ProcessStatusStage).map((status) => ({
    name: t(`SCREENING_INVITATIONS_PROCESS_STATUS_${status}`),
    value: status,
  }));

  const hasQuantitativeTestgroups =
    isEvaluation &&
    testGroups[screeningInstanceId]?.some(
      (testgroup) =>
        testgroup.type === TestGroupTypes.QUANTITATIVE ||
        testgroup.type === TestGroupTypes.CUSTOM ||
        testgroup.type === TestGroupTypes.CHALLENGE_ASYNC_AI
    );

  const getTestGroupMetricDetail = (tg) => useTestGroupMetrics({ testGroup: tg });

  const containerTopRight = () => (
    <>
      {resultsToShow === 1 ? (
        invitationData && isNullOrUndefined(invitationData.score) ? (
          <button
            type="button"
            className="cursor-not-allowed report-btn-disabled-text px-3 py-1 text-xs rounded-0 d-flex align-items-center font-weight-700 bg-white report-btn-disabled-button"
          >
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{t('REPORT_DISABLED_EVALUATION_NOT_COMPLETED_YET')}</Tooltip>}
            >
              <div>
                <span className="report-btn-disabled-text mr-2 ">
                  {t('CANDIDATE_DOWNLOAD_REPORT')}
                </span>
                <CloudDownload fill="#959595" />
              </div>
            </OverlayTrigger>
          </button>
        ) : (
          showDownloadButton() && (
            <DropdownSelect
              key="download-report-selector"
              options={downloadOptions}
              className="w-100 position-relative d-flex align-items-center"
              selected={null}
              selectedPlaceholder
              placeholderClassname="mr-2 text-xs font-weight-500"
              minWidth="12rem"
              axHeight={false}
              placeholder={
                reportLoading ? (
                  <HorizontalLoader className="loader-report ml-4" />
                ) : (
                  t('CANDIDATE_DOWNLOAD_REPORT')
                )
              }
              setState={(e) => {
                if (e?.id !== undefined) selectDownloadReportOption(e.id);
              }}
              disabled={reportLoading || !canDownloadMainReport}
              showDownloadCloud
            />
          )
        )
      ) : (
        <>
          <DropdownSelect
            key="results-relative-company-selector"
            options={[
              {
                id: 0,
                displayName: TestGroupMetricsRol.GENERAL_USER_ROLES,
                visible: testGroups?.[screeningInstanceId]?.some(
                  (testgroup) =>
                    testgroup?.metrics?.dataGrouped?.[0]?.roleMetrics[roleMetricSelected]
                ),
              },
              {
                id: companyId,
                displayName: companyName,
                visible: testGroups?.[screeningInstanceId]?.every(
                  (testgroup) =>
                    testgroup?.metrics?.dataGrouped?.[companyId]?.roleMetrics[roleMetricSelected]
                ),
              },
            ]
              .filter((item) => item.visible)
              .map((item) => ({
                id: item.id,
                name: (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip>
                        {t('METRIC_PROFILE_COMPARE_DETAIL_OVERLAY')?.replace(
                          '{{company}}',
                          item.displayName
                        )}
                      </Tooltip>
                    }
                  >
                    <span>{t(item.displayName)}</span>
                  </OverlayTrigger>
                ),
              }))}
            className="w-100 position-relative"
            selected={companyMetricSelected}
            selectedPlaceholder
            placeholderClassname="mr-2 text-xs font-weight-500"
            axHeight={false}
            placeholder={t('METRIC_PROFILE_COMPARE_DETAIL_OVERLAY')}
            setState={(e) => {
              setCompanyMetricSelected(e?.id);
            }}
            minWidth="12rem"
          />
        </>
      )}
    </>
  );

  const fullTestScore = () => {
    if (allSFIATestGroups(testGroups[screeningInstanceId] || [])) {
      return <span>{sfiaAverage.value}</span>;
    }
    const ponderatedResult = getPonderatedPercentil();
    return (
      <div className="d-flex flex-column">
        <span className="font-weight-bold  mb-2 invited-profile-stage-percent">
          {uniqueProfileData?.recruitSession?.reviewType === ReviewTypeEnum.PSYCHOTECHNICS ? (
            'N/A'
          ) : resultsToShow === 1 ? (
            isEvaluation ? (
              `${Number.isNaN(
                oneDecimal(uniqueProfileData?.data?.[selectedStage]?.invitation?.score)
              )
                ? '-'
                : `${oneDecimal(uniqueProfileData?.data?.[selectedStage]?.invitation?.score)}%`
              }`
            ) : Number.isNaN(
              uniqueProfileData?.data?.find((technology) => technology?.isPrimary)?.bestScore
            ) ? (
              <HorizontalLoader className="loader-classname" />
            ) : (
              `${oneDecimal(
                Math.round(
                  uniqueProfileData?.data?.find((technology) => technology?.isPrimary)?.bestScore
                )
              )}%`
            )
          ) : Number.isNaN(oneDecimal(ponderatedResult)) ? (
            '-'
          ) : (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{t('PONDERATED_PERCENTIL_CANDIDATE_ON_TESTGROUP')}</Tooltip>}
            >
              <span>{getIntegerPart(ponderatedResult)}</span>
            </OverlayTrigger>
          )}
        </span>
      </div>
    );
  };

  const getTestGroupPercentil = (tgId) => {
    const tg = testGroups[screeningInstanceId].find((t) => t.id === tgId);
    const metricDataSelected = tg?.metrics?.dataGrouped?.find(
      (d) => d.companyId === companyMetricSelected
    )?.roleMetrics[roleMetricSelected];
    if (metricDataSelected) {
      return getPercentile(tg.score, metricDataSelected?.percentiles);
    }
    return 0;
  };

  const getTestGroupTotalPercentil = (testGroup) => {
    const { metricDataSelected } = getTestGroupMetricDetail(testGroup);
    const display = {
      percentil: metricDataSelected ? (
        getPercentile(testGroup.score, metricDataSelected?.percentiles)
      ) : (
        <>
          <FontAwesomeIcon icon={faInfoCircle} className="no-metrics-icon" />
        </>
      ),
      overlay: metricDataSelected
        ? 'PERCENTIL_FOR_CANDIDATE_ON_TESTGROUP'
        : 'NO_DATA_ENOUGH_FOR_CANDIDATE_ON_TESTGROUP',
    };
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{t(display.overlay)}</Tooltip>}
        key={`tesgroup-percentil-detail-${companyMetricSelected}`}
      >
        <span
          className="font-weight-bold invited-profile-skill-title pl-4 text-xs points-text no-metrics-icon"
          key={`tg-percentil-in-${display.percentil}`}
        >
          {display.percentil}
        </span>
      </OverlayTrigger>
    );
  };

  const getTestGroupPercentage = (testGroup) => {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip>
            {isEvaluation
              ? `pts ${Math.round(
                (testGroup.score ?? 0) /
                (100 / (testGroup.weight * groupsProportion[screeningInstanceId] ?? 0))
              )}/${Math.round(testGroup.weight * groupsProportion[screeningInstanceId] ?? 0)}`
              : ''}
          </Tooltip>
        }
      >
        <span className="font-weight-bold invited-profile-skill-title pl-4 text-xs points-text">
          {`${Math.round(testGroup.score * 10) / 10} % `}
        </span>
      </OverlayTrigger>
    );
  };

  const getPsycotechnicInfo = (tgId) => {
    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip>{t('RESULTS_NOT_AVAILABLE_PSYCH')}</Tooltip>}
      >
        <span
          className="font-weight-bold invited-profile-skill-title pl-4 text-xs points-text no-metrics-icon"
          key={`tg-psyco-not-${tgId}`}
        >
          <FontAwesomeIcon icon={faInfoCircle} className="psycotechnic-icon" />
        </span>
      </OverlayTrigger>
    );
  };

  useEffect(() => {
    setResultsToShow(1);
    setCompanyMetricSelected(0);
  }, [screeningInstanceId]);

  return (
    <div
      data-testid="invited-profile-stages-container"
      className="px-4 pb-4 pt-0 invited-profile-stages-card flex-grow-1 h-100"
    >
      {!isPublicDashboard && <ProfileInvitationsAndChallenges />}
      {isEvaluation && (
        <>
          {isEvaluation && (
            <div
              className={`d-flex justify-content-between mt-3 ${isPublicDashboard && 'bg-white p-4 b-radius-10'
                }`}
            >
              <div className="d-flex flex-column">
                <span className="text-md font-weight-600">{screeningName}</span>
                <span className="text-sm text-muted font-weight-400">{`${t(
                  'PROFILE_INVITED_DATE'
                )} ${invitedDate} | ${t('PROFILE_SUBMITTED_DATE')} ${submittedDate}`}</span>
              </div>
              <div className="d-flex flex-column justify-content-end">
                <span>{t('PROFILE_EVALUATION_STATUS')}</span>
                <DropdownSelectDot
                  className={`py-0 font-montserrat text-12-px border-blue-principal b-radius-8 ${isPublicDashboard && 'bg-grey'
                    }`}
                  placeholder={t('DASHBOARD_INVITATIONS_PROCESS_STATUS')}
                  listSelect
                  arrowLightBlue
                  options={processStatusOptions}
                  dot
                  selectedPlaceholder
                  selected={uniqueProfileData?.data?.[0]?.invitation?.processStatus}
                  setState={(e) =>
                    updateStageSingleCandidate(e.id, uniqueProfileData?.data?.[0]?.invitation?.id)
                  }
                  padding={false}
                  fullWidth={false}
                  alignCenter={'align-self-end align-self-sm-center'}
                  disabled={isPublicDashboard}
                />
              </div>
            </div>
          )}
        </>
      )}

      <div
        className={`card-profile description-container position-relative h-100 bg-white b-radius-8 mt-3`}
      >
        <div className="d-flex flex-column invited-profile-stages-container p-4">
          <div className="d-flex justify-content-between">
            <DropdownSelect
              key="results-profile-selector"
              minWidth="12rem"
              options={[
                {
                  id: 1,
                  displayName: 'PROFILE_QUANTITAVE_RESULTS',
                  visible: true,
                  overlay: 'PROFILE_QUANTITAVE_RESULTS_OVERLAY',
                },
                {
                  id: 2,
                  displayName: 'PROFILE_RELATIVE_RESULTS',
                  visible:
                    testGroups[screeningInstanceId]?.some(
                      (testgroup) => testgroup?.metrics?.dataGrouped?.length > 0
                    ) && uniqueProfileData?.data?.[selectedStage]?.invitation?.score,
                  overlay: 'PROFILE_RELATIVE_RESULTS_OVERLAY',
                },
              ]
                .filter((item) => item.visible)
                .map((item) => ({
                  id: item.id,
                  name: (
                    <OverlayTrigger placement="top" overlay={<Tooltip>{t(item.overlay)}</Tooltip>}>
                      <span>{t(item.displayName)}</span>
                    </OverlayTrigger>
                  ),
                }))}
              className="w-100 position-relative"
              selected={resultsToShow}
              selectedPlaceholder
              placeholderClassname="mr-2 text-xs font-weight-500"
              placeholder={t('SELECT_RESULT_TYPE')}
              disabled={isNullOrUndefined(
                uniqueProfileData?.data?.[selectedStage]?.invitation?.score
              )}
              setState={(e) => {
                setResultsToShow(e?.id);
              }}
            />
            {isEvaluation && containerTopRight()}
          </div>
          {isChallenge || invitationData?.score || invitationData?.score === 0 ? (
            <div className="d-flex mt-3 px-3 flex-column flex-sm-row">
              {(hasQuantitativeTestgroups || isChallenge) && (
                <div className="pr-sm-5 text-center d-flex flex-column">
                  <Progress
                    type="circle"
                    strokeWidth={4}
                    strokeColor={{
                      '30%': '#4987BE',
                      '100%': '#9BF558',
                    }}
                    className="mb-3 mb-lg-0"
                    width={140}
                    percent={percentValue}
                    format={() => fullTestScore()}
                  />
                  {process.env.RAZZLE_RUNTIME_IS_PROCTORING_ENABLED === 'true' &&
                    isEvaluation &&
                    uniqueProfileData?.data?.[0]?.recruitSessionStage?.proctoringEnabled &&
                    screeningInstanceId !== undefined &&
                    uniqueProfileData?.data?.[selectedStage]?.invitation?.endDate && (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            {publicDashboardGuest?.accessType ===
                              DashboardAccessLevel.LIMITED_ACCESS
                              ? t('PUBLIC_DASHBOARD_NOT_ENOUGH_ACCESS')
                              : t('PROCTORING_DETAIL_INFORMATION')}
                          </Tooltip>
                        }
                      >
                        <div className="d-flex flex-column mt-3">
                          {Boolean(
                            uniqueProfileData?.data?.[selectedStage]?.invitation?.totalEvents
                          ) && (
                              <span className="text-nowrap  text-black text-xs font-weight-600 mb-1">
                                {t('ASSESSMENT_INVITATION_TOTAL_EVENTS').replace(
                                  '{totalEvents}',
                                  uniqueProfileData?.data?.[selectedStage]?.invitation?.totalEvents
                                )}
                              </span>
                            )}
                          <Link
                            to={`${(isPublicDashboard
                              ? PUBLIC_INVITED_PROFILES_PROCTORING_DETAIL
                              : INVITED_PROFILES_PROCTORING_DETAIL
                            )
                              .replace(
                                ':recruitSessionId',
                                uniqueProfileData?.data?.[0]?.recruitSessionStage?.recruitSessionId
                              )
                              .replace(':profileId', profile?.id)}?workspace=${currentWorkspace}`}
                            className={`text-purple text-xs text-nowrap font-weight-600 ${publicDashboardGuest?.accessType ===
                                DashboardAccessLevel.LIMITED_ACCESS
                                ? 'proctoring-link-disabled'
                                : ''
                              }`}
                            disabled={
                              publicDashboardGuest?.accessType ===
                              DashboardAccessLevel.LIMITED_ACCESS
                            }
                            onClick={(e) => {
                              if (
                                publicDashboardGuest?.accessType ===
                                DashboardAccessLevel.LIMITED_ACCESS
                              ) {
                                e.preventDefault();
                              }
                            }}
                          >
                            {t('VIEW_PROCTORING_ANALYSIS')}
                          </Link>
                        </div>
                      </OverlayTrigger>
                    )}
                  {isChallenge && (
                    <span className=" font-weight-600 text-xs mt-2">
                      {
                        uniqueProfileData?.data?.find((technology) => technology?.isPrimary)
                          ?.technology?.displayName
                      }
                    </span>
                  )}
                </div>
              )}
              <div className="section-container">
                <div
                  className="d-flex flex-column"
                  key={`testgroup-results-${resultsToShow}-${companyMetricSelected}`}
                >
                  {isEvaluation ? (
                    <div className="d-flex flex-column justify-content-between mb-2">
                      <div className="d-flex flex-column">
                        <span
                          data-testid="invited-profile-stages-screening-name"
                          className=" text-xs font-weight-bold text-capitalize"
                        >
                          {screeningName}
                        </span>
                      </div>
                      {loadingTestGroups ? (
                        <Spinner show />
                      ) : hasQuantitativeTestgroups ? (
                        <div data-testid="quantitative-tests-container" key={nanoid()}>
                          {prefilterTestGroupPreview('TESTGROUP_PRE_FILTER_TITLE_NAME')}
                          {testGroups[screeningInstanceId]
                            ?.filter((testgroup) =>
                              [
                                TestGroupTypes.QUANTITATIVE,
                                TestGroupTypes.CHALLENGE_ASYNC_AI,
                                TestGroupTypes.CUSTOM,
                              ].includes(testgroup.type)
                            )
                            ?.map((testGroup, index) => {
                              const progressPercentage = getTestGroupPercentil(testGroup?.id);
                              const progressResult = () =>
                                uniqueProfileData?.recruitSession?.reviewType ===
                                  ReviewTypeEnum.PSYCHOTECHNICS
                                  ? getPsycotechnicInfo(testGroup?.id)
                                  : resultsToShow === 1
                                    ? getTestGroupPercentage(testGroup)
                                    : getTestGroupTotalPercentil(testGroup);
                              return (
                                <>
                                  <div className="d-flex flex-column mt-3" key={nanoid()}>
                                    <div className="d-flex flex-row align-items-center bg-white">
                                      <span className=" text-xs font-weight-bold text-capitalize container-name-mw">
                                        {testGroup?.nameTranslated?.[langKey] || testGroup.name}
                                      </span>
                                      {uniqueProfileData?.recruitSession?.reviewType !==
                                        ReviewTypeEnum.PSYCHOTECHNICS && (
                                          <button
                                            className="border-0 bg-transparent cursor-pointer text-decoration-none d-flex button-detail-mw"
                                            onClick={() =>
                                              getDataSubmission(screeningInstanceId, testGroup, index)
                                            }
                                            type="button"
                                          >
                                            {!(loadingDetail === index) ? (
                                              <span className=" font-weight-600 span-see-detail-modal text-xs ml-2">
                                                {t('DEV_PROFILE_TESTGROUP_VIEW_DETAIL')}
                                              </span>
                                            ) : (
                                              <Spin
                                                indicator={
                                                  <LoadingOutlined
                                                    style={{
                                                      fontSize: 18,
                                                    }}
                                                    spin
                                                  />
                                                }
                                                className="ml-2"
                                              />
                                            )}
                                          </button>
                                        )}
                                    </div>

                                    <Progress
                                      key={`testgroup-results-progress-${resultsToShow}-${companyMetricSelected}`}
                                      strokeWidth={8}
                                      className="invited-profile-stages-progress"
                                      strokeColor={{
                                        '30%': '#4987BE',
                                        '100%': '#9BF558',
                                      }}
                                      percent={
                                        uniqueProfileData?.recruitSession?.reviewType !==
                                          ReviewTypeEnum.PSYCHOTECHNICS
                                          ? resultsToShow === 1
                                            ? testGroup.score ?? 0
                                            : progressPercentage
                                          : 100
                                      }
                                      format={() => progressResult()}
                                    />
                                  </div>
                                </>
                              );
                            })}
                        </div>
                      ) : (
                        <span className=" text-xs font-weight-400 mt-2">
                          {t('QUANTITATIVE_TEST_EMPTY_STATE')}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex flex-column justify-content-between mb-2">
                      <div className="">
                        {uniqueProfileData?.data?.map((progressTech, index) => (
                          <>
                            <div className="d-flex flex-column mt-3" key={nanoid()}>
                              <div className="invited-profile-stages-progress d-flex flex-row align-items-center justify-content-between">
                                <span className=" text-xs font-weight-bold">
                                  {progressTech?.technology?.displayName}
                                </span>
                                <div className="d-flex align-items-center justify-content-center">
                                  <button
                                    className="border-0 bg-transparent cursor-pointer text-decoration-none d-flex"
                                    onClick={() => {
                                      setProfileNotInvited(progressTech, index);
                                    }}
                                    type="button"
                                  >
                                    {loadingDetail !== index ? (
                                      <span className=" font-weight-600 span-see-detail-modal text-xs ml-2">
                                        {t('DEV_PROFILE_TESTGROUP_VIEW_DETAIL')}
                                      </span>
                                    ) : (
                                      <Spin
                                        indicator={
                                          <LoadingOutlined
                                            style={{
                                              fontSize: 18,
                                            }}
                                            spin
                                          />
                                        }
                                        className="ml-2"
                                      />
                                    )}
                                  </button>
                                </div>
                              </div>
                              <Progress
                                strokeWidth={8}
                                className="invited-profile-stages-progress"
                                format={() => { }}
                                strokeColor={{
                                  '30%': '#4987BE',
                                  '100%': '#9BF558',
                                }}
                                percent={Math.round(progressTech?.bestScore)}
                              />
                            </div>
                          </>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : invitationData?.prefilterScore ||
            (invitationData?.prefilterScore >= 0 && invitationData?.prefilterScore !== null) ? (
            <>
              <div className="d-flex mt-3 p-3 flex-column flex-sm-row">
                <div className="pr-sm-3 text-center d-flex flex-column mb-3">
                  <div className="rounded-circle circle-no-data d-flex flex-column justify-content-center">
                    <p className=" text-base font-weight-400 text-center mb-0">
                      {t('CANDIDATE_DETAIL_NO_TEST_TAKEN')}
                    </p>
                  </div>
                </div>
                <div className="section-container">
                  <div className="d-flex flex-column">
                    <span className=" text-xs font-weight-bold text-capitalize">
                      {screeningName}
                    </span>
                    {invitationData.prefilterScore === 1 ? (
                      (invitationData?.limitDate &&
                        new Date() < new Date(invitationData.limitDate)) ||
                        (new Date() <
                          new Date(uniqueProfileData?.data?.[0]?.recruitSessionStage?.endDate) &&
                          isNullOrUndefined(invitationData?.limitDate)) ? (
                        <p className=" text-xs mt-3">
                          {t('PREFILTER_TEST_PASSED_PREVIEW_NOT_STARTED')}
                        </p>
                      ) : (
                        <p className=" text-xs mt-3">
                          {t('DASHBOARD_SEARCH_HEADERS_SCREENINGS')}: {t(`SEARCH_EXPIRED`)}
                        </p>
                      )
                    ) : invitationData.prefilterScore === 0.5 ? (
                      <p className=" text-xs mt-3">{t('PREFILTER_TEST_REVIEW_PREVIEW')}</p>
                    ) : (
                      <p className=" text-xs mt-3">{t('PREFILTER_TEST_NOT_PASSED_PREVIEW')}</p>
                    )}
                  </div>
                  <>{prefilterTestGroupPreview('TESTGROUP_PRE_FILTER_TITLE_NAME')}</>
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex mt-3 p-3 flex-column flex-sm-row">
              <div className="pr-sm-3 text-center d-flex flex-column mb-3">
                <div className="rounded-circle circle-no-data d-flex flex-column justify-content-center">
                  <p className=" text-base font-weight-400 text-center mb-0">
                    {t('CANDIDATE_DETAIL_NO_DATA')}
                  </p>
                </div>
              </div>
              <div className="section-container">
                <div className="d-flex flex-column">
                  <span className=" text-xs font-weight-bold text-capitalize">{screeningName}</span>
                  <p className=" text-xs mt-3">
                    {t('DASHBOARD_SEARCH_HEADERS_SCREENINGS')}
                    {': '}
                    {invitationData?.endDate || !isNullOrUndefined(invitationData?.score)
                      ? t('SCREENINGS_INVITATIONS_PROCESSING')
                      : t(`SCREENINGS_INVITATIONS_${invitationData?.status}`)}
                  </p>
                  <p className=" text-xs">{t('CANDIDATE_DETAIL_EMPTY_STATE_TEXT')}</p>
                </div>
              </div>
            </div>
          )}
          {!isInvitedProfile && (
            <>
              {Boolean(visibleTechnologies?.length) && (
                <div className="mt-5 d-flex">
                  <span className=" font-weight-600 text-xs technologies-text">
                    {t('PROFILE_TECHNOLOGIES_USER_KNOWS')}
                    <span className=" font-weight-500 text-xs mx-1 mb-1 technology-text-value">
                      {visibleTechnologies
                        ?.filter((tech) => tech.technologyType === 'KNOW')
                        ?.map(
                          (tech, i) =>
                            ` ${tech?.technology?.displayName}${i ===
                              visibleTechnologies.filter(
                                (visibleTech) => visibleTech.technologyType === 'KNOW'
                              )?.length -
                              1
                              ? ''
                              : ','
                            } `
                        )}
                    </span>
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {optionsCollapsed
        ?.filter((optionsCollapsed) => optionsCollapsed.isVisible)
        .map((options, index) => (
          <Collapsed
            key={index}
            collapsedSelected={collapsedSelected}
            setCollapsedSelected={setCollapsedSelected}
            index={index}
            options={options}
            collapsedEnabled={options.collapsedEnabled}
            containerClassname={options.containerClassname}
            titleClassnames={options.titleClassnames}
            maxHeight={'30rem'}
          />
        ))}
      <Modal
        className="screening-profile-comment-modal"
        open={modalCandidateRole}
        onCancel={() => {
          setModalCandidateRole(false);
          setCandidateRole('');
        }}
        closeIcon={<img src={closeIcon} alt={t('CLOSE_ALT')} />}
        footer={
          <button
            onClick={addCandidateRole}
            className="rounded-0 font-weight-bold d-flex align-items-center border-0 outline-none px-4 py-2  text-white bg-maya-blue"
            type="button"
            disabled={!screeningInstance?.candidateRole && candidateRole?.trim()?.length < 10}
          >
            {t('DOWNLOAD_REPORT_MODAL_BUTTON')}
          </button>
        }
      >
        <h3>
          {screeningInstance?.candidateRole
            ? t('SHOW_ROLE_MODAL_TITLE')
            : t('NEW_ROLE_MODAL_TITLE')}
        </h3>
        <input
          id="candidateRole"
          className="rounded p-2 outline-none w-100 mt-4 border candidate-role-input-height text-xs"
          placeholder={t('ADD_ROLE_MODAL_PLACEHOLDER')}
          value={screeningInstance?.candidateRole ?? candidateRole}
          onChange={(e) => {
            const { value } = e.target;
            if (value.length < 50) {
              setCandidateRole(value);
              if (value.trim().length < 10) {
                if (!roleError) {
                  setRoleError(t('CANDIDATE_ROLE_TOO_SHORT_ERROR'));
                }
              } else {
                setRoleError('');
              }
            } else {
              setRoleError(t('CREATE_PREFILTER_MAX_CHAR_ANSWER'));
            }
          }}
          type="text"
          maxLength={50}
          disabled={screeningInstance?.candidateRole}
        />
        {roleError && (
          <p className="danger-text text-xs m-0 mt-1 p-0" data-testid="candidate-role-input-error">
            {roleError}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default InvitedProfileStages;
